import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "@/components/sections/Agenda.css";
import { AGENDA_ITEMS as ITEMS } from "@/components/sections/agendaItems";
import { AgendaItem } from "@/components/sections/AgendaItem";
import { AgendaIntro, AgendaBackground } from "@/components/sections/AgendaIntro";
import { usePointerSpotlight } from "@/hooks/usePointerSpotlight";
import { useStaggeredReveal } from "@/hooks/useStaggeredReveal";
import { useRovingKeyboardNav } from "@/hooks/useRovingKeyboardNav";

const SPOTLIGHT = { xVar: "--cfa-x", yVar: "--cfa-y", restX: "50%", restY: "45%" };
const REVEAL = { threshold: 0.18, stepMs: 90 };

// Vertical progress fill that tracks the active item along the timeline track.
function useTimelineProgress(trackRef, active) {
  const [fillHeight, setFillHeight] = useState(0);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const ratio = ITEMS.length > 1 ? active / (ITEMS.length - 1) : 0;
    setFillHeight(track.getBoundingClientRect().height * ratio);
  }, [trackRef, active]);

  useLayoutEffect(() => {
    update();
    // Recompute after the card-expand transition changes the timeline height.
    const t = setTimeout(update, 460);
    return () => clearTimeout(t);
  }, [update]);

  useEffect(() => {
    window.addEventListener("resize", update, { passive: true });
    return () => window.removeEventListener("resize", update);
  }, [update]);

  return fillHeight;
}

const Agenda = () => {
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const trackRef = useRef(null);
  const itemRefs = useRef([]);
  const buttonRefs = useRef([]);

  const fillHeight = useTimelineProgress(trackRef, active);
  const revealed = useStaggeredReveal(itemRefs, ITEMS.length, REVEAL);
  const spotlight = usePointerSpotlight(rootRef, SPOTLIGHT);

  const selectItem = useCallback((index, moveFocus = false) => {
    if (index < 0 || index >= ITEMS.length) return;
    setActive(index);
    if (moveFocus && buttonRefs.current[index]) buttonRefs.current[index].focus();
  }, []);

  const onKeyDown = useRovingKeyboardNav(ITEMS.length, selectItem, { axis: "vertical" });

  return (
    <section
      ref={rootRef}
      className="cpf-agenda-motion has-js"
      id="agenda"
      aria-labelledby="cpf-agenda-title"
      data-testid="agenda-section"
      onPointerMove={spotlight.onPointerMove}
      onPointerLeave={spotlight.onPointerLeave}
    >
      <AgendaBackground />

      <div className="cfa-container">
        <div className="cfa-grid">
          <AgendaIntro />

          <div className="cfa-timeline-shell">
            <div className="cfa-timeline-shell__top">
              <strong>Thursday, 1 October 2026</strong>
              <span>Interactive programme timeline</span>
            </div>

            <ol className="cfa-agenda-list">
              <li className="cfa-timeline-track" ref={trackRef} aria-hidden="true" />
              <li className="cfa-timeline-fill" style={{ height: `${fillHeight}px` }} aria-hidden="true" />

              {ITEMS.map((item, i) => (
                <AgendaItem
                  key={item.title}
                  item={item}
                  index={i}
                  active={active === i}
                  revealed={revealed.has(i)}
                  itemRef={(el) => (itemRefs.current[i] = el)}
                  buttonRef={(el) => (buttonRefs.current[i] = el)}
                  onSelect={() => selectItem(i)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                />
              ))}
            </ol>

            <p className="cfa-selection-status" aria-live="polite">{ITEMS[active].title} selected.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Agenda;
